import type { ColumnDef, ColumnFiltersState } from "@gately/ui/data-table";
import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Download } from "lucide-react";
import * as XLSX from "xlsx";
import { z } from "zod/v4";

import { Button } from "@gately/ui/button";
import { DataTable } from "@gately/ui/data-table";
import { DateTimePicker } from "@gately/ui/date-time-picker";
import { toast } from "@gately/ui/toast";
import { entryReasonFilterSchema, entrySourceSchema } from "@gately/validators";

import {
  activitySheetRows,
  dateKey,
  ENTRY_REASON_OPTIONS,
  reasonLabel,
  RESULT_LABELS,
  sourceLabel,
  sourceLabels,
} from "~/lib/dashboard-format";
import { orpc, orpcClient } from "~/lib/orpc";
import { resolvePermissions } from "~/lib/use-permissions";
import { useTerms } from "~/lib/use-terms";

const DATE_KEY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const COL = z.string().min(1).optional().catch(undefined);

const searchSchema = z.object({
  q: COL,
  result: z
    .array(z.enum(["allow", "deny"]))
    .optional()
    .catch(undefined),
  from: DATE_KEY.optional().catch(undefined),
  to: DATE_KEY.optional().catch(undefined),
  unit: COL,
  gate: COL,
  operator: COL,
  // Closed vocabulary. An old bookmarked free-text `?reason=` is dropped by
  // `.catch`, not a 400.
  reason: z.array(entryReasonFilterSchema).optional().catch(undefined),
  // From the shared vocabulary, so a chip the toolbar renders is always a
  // value this schema keeps (an unknown one makes `.catch` drop the whole
  // param) and the server's input schema accepts.
  source: z.array(entrySourceSchema).optional().catch(undefined),
});

type ActivitySearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_dashboard/activite")({
  validateSearch: (s) => searchSchema.parse(s),
  // Org-wide activity, which a résidence can withhold from its own syndic
  // (`viewAllActivity`) — the server 403s `activity.listForResidence` then.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    if (!resolvePermissions(me).canViewActivity) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: ActivitePage,
});

const PAGE_SIZE = 50;

function filterInput(search: ActivitySearch) {
  return {
    ...(search.q ? { q: search.q } : {}),
    ...(search.result ? { result: search.result } : {}),
    ...(search.from ? { from: search.from } : {}),
    ...(search.to ? { to: search.to } : {}),
    ...(search.unit ? { unit: search.unit } : {}),
    ...(search.gate ? { gate: search.gate } : {}),
    ...(search.operator ? { operator: search.operator } : {}),
    ...(search.reason ? { reason: search.reason } : {}),
    ...(search.source ? { source: search.source } : {}),
  };
}

/** Column id → URL search param. The grid's in-column filters ARE the URL, so
 *  a filtered view is shareable and survives a reload; the server does the
 *  actual filtering (this table is keyset-paginated, so it must). */
const FILTER_PARAM = {
  guestName: "q",
  unitLabel: "unit",
  gateName: "gate",
  operatorName: "operator",
  result: "result",
  reason: "reason",
  source: "source",
} as const satisfies Record<string, keyof ActivitySearch>;

type ActivityRow = Awaited<
  ReturnType<typeof orpcClient.activity.listForResidencePaged>
>["items"][number];

function ActivitePage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const terms = useTerms();

  // Search box → ?q= after 300 ms. `q` is the visitor-name field, so the
  // Visiteur column has no separate control.
  const [q, setQ] = useState(search.q ?? "");
  useEffect(() => {
    const t = setTimeout(() => {
      const next = q.trim() || undefined;
      if (next !== search.q) {
        void navigate({
          search: (prev) => ({ ...prev, q: next }),
          replace: true,
        });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q, search.q, navigate]);

  // The grid's filters read from and write to the URL.
  const columnFilters = useMemo<ColumnFiltersState>(
    () =>
      Object.entries(FILTER_PARAM).flatMap(([id, param]) => {
        const value = search[param];
        if (!value) return [];
        // Faceted params arrive as a repeated search param → already an array.
        return [{ id, value }];
      }),
    [search],
  );

  function setColumnFilters(next: ColumnFiltersState) {
    // Clear every managed param first, then apply what survived — otherwise a
    // removed filter would linger in the URL.
    const patch: Record<string, string | string[] | undefined> = {};
    for (const param of Object.values(FILTER_PARAM)) patch[param] = undefined;
    for (const f of next) {
      if (!(f.id in FILTER_PARAM)) continue;
      const param = FILTER_PARAM[f.id as keyof typeof FILTER_PARAM];
      if (Array.isArray(f.value)) {
        patch[param] = f.value.length > 0 ? (f.value as string[]) : undefined;
      } else {
        patch[param] = (f.value as string) || undefined;
      }
    }
    void navigate({
      // Values are re-validated by `searchSchema` on read, which `.catch()`es
      // anything that is not a legal enum member back to undefined.
      search: (prev) => ({ ...prev, ...patch }),
      replace: true,
    });
  }

  const filters = filterInput(search);

  const query = useInfiniteQuery({
    queryKey: [...orpc.activity.listForResidencePaged.key(), filters],
    queryFn: ({ pageParam }) =>
      orpcClient.activity.listForResidencePaged({
        ...filters,
        limit: PAGE_SIZE,
        cursor: pageParam ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
  });

  const rows = query.data?.pages.flatMap((p) => p.items) ?? [];

  const columns: ColumnDef<ActivityRow>[] = [
    {
      accessorKey: "createdAt",
      size: 150,
      minSize: 130,
      enableHiding: false,
      meta: { label: "Date" },
      header: "Date",
      cell: ({ getValue }) => (
        <span className="text-muted-foreground whitespace-nowrap">
          {new Date(getValue<string>()).toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      ),
    },
    {
      accessorKey: "guestName",
      size: 210,
      minSize: 150,
      enableHiding: false,
      meta: { label: "Visiteur" },
      header: "Visiteur",
    },
    {
      accessorKey: "unitLabel",
      size: 170,
      minSize: 130,
      meta: {
        label: terms.unitRowLabel,
        filter: { kind: "text", placeholder: "Filtrer…" },
      },
      header: terms.unitRowLabel,
    },
    {
      accessorKey: "gateName",
      size: 160,
      minSize: 120,
      meta: {
        label: "Portail",
        filter: { kind: "text", placeholder: "Portail" },
      },
      header: "Portail",
      cell: ({ getValue }) => getValue<string | null>() ?? "—",
    },
    {
      accessorKey: "operatorName",
      size: 170,
      minSize: 130,
      meta: {
        label: "Opérateur",
        filter: { kind: "text", placeholder: "Opérateur" },
      },
      header: "Opérateur",
      cell: ({ getValue }) => getValue<string | null>() ?? "—",
    },
    {
      accessorKey: "result",
      size: 140,
      minSize: 120,
      meta: { label: "Résultat" },
      header: "Résultat",
      cell: ({ getValue }) => {
        const v = getValue<ActivityRow["result"]>();
        return (
          <span
            className={
              v === "allow" ? "text-(--success-700)" : "text-destructive"
            }
          >
            {RESULT_LABELS[v] ?? v}
          </span>
        );
      },
    },
    {
      accessorKey: "reason",
      size: 180,
      minSize: 130,
      meta: { label: "Motif" },
      header: "Motif",
      // Manual mode: a client sort would only reorder the loaded page.
      enableSorting: false,
      cell: ({ getValue }) => {
        const v = getValue<string | null>();
        return (
          <span className="text-muted-foreground">
            {v ? reasonLabel(v) : "—"}
          </span>
        );
      },
    },
    {
      accessorKey: "source",
      size: 170,
      minSize: 130,
      meta: { label: "Source" },
      header: "Source",
      cell: ({ getValue }) => (
        <span className="text-muted-foreground">
          {sourceLabel(getValue<string>(), terms)}
        </span>
      ),
    },
  ];

  const [exporting, setExporting] = useState(false);
  async function exportAs(bookType: "xlsx" | "csv") {
    setExporting(true);
    try {
      const data = await orpcClient.activity.exportForResidence(filters);
      if (data.length === 0) {
        toast.error("Aucune entrée à exporter.");
        return;
      }
      const sheet = XLSX.utils.aoa_to_sheet(activitySheetRows(data));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, sheet, "Activité");
      const stamp = dateKey(new Date()).replaceAll("-", "");
      XLSX.writeFile(wb, `activite-${stamp}.${bookType}`, { bookType });
      toast.success(
        data.length === 5000
          ? "Export limité aux 5 000 entrées les plus récentes."
          : "Export téléchargé.",
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Échec de l'export.");
    } finally {
      setExporting(false);
    }
  }

  function setFilter(patch: Partial<ActivitySearch>) {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  }

  return (
    <div className="px-7 py-6">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-foreground text-2xl font-semibold tracking-[-0.02em]">
            Activité
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Toutes les entrées au portail {terms.ofTheOrg}.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={exporting}
            onClick={() => void exportAs("csv")}
          >
            <Download size={14} strokeWidth={1.85} />
            CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={exporting}
            onClick={() => void exportAs("xlsx")}
          >
            <Download size={14} strokeWidth={1.85} />
            XLSX
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        isLoading={query.isPending}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        initialPinning={{ left: ["createdAt"] }}
        showColumnFilters
        showColumnToggle
        search={{
          value: q,
          onChange: setQ,
          placeholder: "Rechercher un visiteur…",
        }}
        filters={[
          {
            columnId: "result",
            label: "Résultat",
            options: [
              { value: "allow", label: RESULT_LABELS.allow ?? "Autorisé" },
              { value: "deny", label: RESULT_LABELS.deny ?? "Refusé" },
            ],
          },
          {
            columnId: "source",
            label: "Source",
            options: Object.entries(sourceLabels(terms)).map(
              ([value, label]) => ({
                value,
                label,
              }),
            ),
          },
          {
            columnId: "reason",
            label: "Motif",
            options: ENTRY_REASON_OPTIONS,
          },
        ]}
        toolbar={
          <>
            <DateTimePicker
              mode="date"
              className="h-9 w-36"
              value={
                search.from ? new Date(`${search.from}T00:00:00`) : undefined
              }
              onChange={(d) => setFilter({ from: d ? dateKey(d) : undefined })}
              placeholder="Du"
            />
            <DateTimePicker
              mode="date"
              className="h-9 w-36"
              value={search.to ? new Date(`${search.to}T00:00:00`) : undefined}
              onChange={(d) => setFilter({ to: d ? dateKey(d) : undefined })}
              placeholder="Au"
            />
            {search.from || search.to ? (
              <button
                type="button"
                onClick={() => setFilter({ from: undefined, to: undefined })}
                className="text-muted-foreground hover:text-foreground text-sm font-medium transition-colors"
              >
                Effacer les dates
              </button>
            ) : null}
          </>
        }
        empty={{ title: "Aucune entrée enregistrée." }}
        manual={{
          hasNextPage: query.hasNextPage,
          isFetchingNextPage: query.isFetchingNextPage,
          onLoadMore: () => void query.fetchNextPage(),
        }}
      />
    </div>
  );
}
